import React, {ReactNode} from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {AppState} from 'store/customer/storeSetup';
import {
    getHasWebGLError,
    setHasWebGLError,
} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {isWebGLRelatedError} from 'components/customer/Preview3DCommon/lib/isWebGLRelatedError';
import WebGLUnavailableMessage from 'components/customer/Preview3DCommon/components/WebGLUnavailableMessage';

// Redux mapStateToProps
const mapState = (state: AppState) => ({
    hasWebGLError: getHasWebGLError(state),
});

// Redux mapDispatchToProps
const mapDispatch = {
    setHasWebGLError,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Preview3DErrorBoundaryProps extends PropsFromRedux {
    children: ReactNode;
    bordered?: boolean;
    fullScreen?: boolean;
    resetKey?: string | number;
}

interface Preview3DErrorBoundaryState {
    hasError: boolean;
    isWebGLError: boolean;
}

/**
 * Error boundary implementation for Preview3D components
 * @param {Preview3DErrorBoundaryProps} props:Preview3DErrorBoundaryProps
 * @return {React.ReactNode}
 */
class Preview3DErrorBoundary extends React.Component<
    Preview3DErrorBoundaryProps,
    Preview3DErrorBoundaryState
> {
    /**
     * Constructor
     * @param {Preview3DErrorBoundaryProps} props:Preview3DErrorBoundaryProps
     * @return {void}
     */
    constructor(props: Preview3DErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            isWebGLError: false,
        };
    }

    /**
     * Handles setting of state when error occured
     * @param {Error} error:Error
     * @return {void}
     */
    static getDerivedStateFromError(error: Error): Preview3DErrorBoundaryState {
        // Return the new state with the error and WebGL error status
        return {
            hasError: true,
            isWebGLError: isWebGLRelatedError(error),
        };
    }

    /**
     * Handle catching of exceptions
     * @return {void}
     */
    componentDidCatch() {
        const {isWebGLError} = this.state;

        // Dispatch the Redux action if it's a WebGL error
        if (isWebGLError) {
            const {setHasWebGLError} = this.props;
            setHasWebGLError(true);
        }
    }

    /**
     * Auto-recover when the caller bumps `resetKey` (e.g. after an
     * orientation flip).
     * @param {Preview3DErrorBoundaryProps} prevProps
     * @return {void}
     */
    componentDidUpdate(prevProps: Preview3DErrorBoundaryProps) {
        const {resetKey, hasWebGLError, setHasWebGLError} = this.props;
        const {hasError} = this.state;

        if (
            resetKey !== undefined &&
            resetKey !== prevProps.resetKey &&
            (hasError || hasWebGLError)
        ) {
            this.setState({hasError: false, isWebGLError: false});
            if (hasWebGLError) {
                setHasWebGLError(false);
            }
        }
    }

    /**
     * Render the React element
     * @return {React.ReactNode}
     */
    render() {
        const {children, hasWebGLError, bordered, fullScreen} = this.props;
        const {hasError, isWebGLError} = this.state;

        if (isWebGLError || hasWebGLError) {
            return (
                <WebGLUnavailableMessage
                    bordered={bordered}
                    fullScreen={fullScreen}
                />
            );
        }

        if (hasError) {
            return <h1>Something went wrong.</h1>;
        }

        return children;
    }
}

export default connector(Preview3DErrorBoundary);
